import { useEffect, useState } from "react";
import {
    User, Calendar, Clock, AlertCircle, Tag,
    ArrowLeft, Search, Filter, Eye, X, FileText
} from "lucide-react";
import Swal from "sweetalert2";
import config from "../../config";
import TicketModal from "./TicketModal";

export default function TaskTicket({ handlePageChange, currentUser }) {
    const [tickets, setTickets] = useState([]);
    const [filteredTickets, setFilteredTickets] = useState({
        pending: [],
        inProgress: [],
        resolved: [],
        closed: []
    });
    const [search, setSearch] = useState("");
    const [draggedTicket, setDraggedTicket] = useState(null);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [showModal, setShowModal] = useState(false);

    const API_TICKETS = `${config.API_BASE_URL}/Tickets`;


    useEffect(() => { loadTickets(); }, []);
    useEffect(() => { organizeTickets(tickets, search); }, [search, tickets]);

    // ===== ORGANIZE TICKETS BY STATUS =====
    const organizeTickets = (ticketList, searchTerm) => {
        const filtered = ticketList.filter(ticket =>
            ticket.ticketNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            ticket.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            ticket.category?.toLowerCase().includes(searchTerm.toLowerCase())
        );

        setFilteredTickets({
            pending: filtered.filter(t => t.status === "Pending"),
            inProgress: filtered.filter(t => t.status === "In Progress"),
            resolved: filtered.filter(t => t.status === "Resolved"),
            closed: filtered.filter(t => t.status === "Closed")
        });
    };

    // ===== DRAG & DROP HANDLERS =====
    const handleDragStart = (e, ticket) => {
        setDraggedTicket(ticket);
        e.currentTarget.style.opacity = '0.5';
    };

    const handleDragEnd = (e) => {
        e.currentTarget.style.opacity = '1';
        setDraggedTicket(null);
    };

    const handleDragOver = (e) => { e.preventDefault(); };

    // ===== LOAD TICKETS =====
    const loadTickets = async () => {
        try {
            const res = await fetch(API_TICKETS);
            if (!res.ok) throw new Error();
            const data = await res.json();

            const adminData = JSON.parse(localStorage.getItem("admin") || "{}");
            const userData = JSON.parse(localStorage.getItem("user") || "{}");
            const userRole = localStorage.getItem("userRole");

            const currentFullName =
                currentUser?.fullNames ||
                adminData?.fullNames ||
                userData?.fullNames ||
                null;

            const currentUsername =
                currentUser?.username ||
                adminData?.username ||
                userData?.username ||
                null;

            console.log("=== CURRENT USER INFO ===");
            console.log("Role:", adminData?.role || userRole || "unknown");
            console.log("Resolved fullName:", currentFullName);

            let myTickets = [];

            if (adminData?.role === "superadmin") {
                myTickets = data;
                console.log("Mode: SUPERADMIN — showing all tickets");
            } else if (currentFullName) {
                myTickets = data.filter(ticket => {
                    if (!ticket.assignees) return false;
                    const assignee = ticket.assignees.toLowerCase();
                    const matchName = currentFullName && assignee.includes(currentFullName.toLowerCase());
                    const matchUser = currentUsername && assignee.includes(currentUsername.toLowerCase());
                    return matchName || matchUser;
                });
                console.log(`Mode: FILTERED — showing tickets for "${currentFullName}"`);
            } else {
                myTickets = [];
                console.warn("Mode: NO USER IDENTIFIED — empty tickets");
            }

            console.log(`=== MY TICKETS (${myTickets.length} total) ===`);
            myTickets.forEach(ticket => {
                console.log(
                    `%c${ticket.ticketNumber}%c [${ticket.status}] → %c${ticket.assignees}`,
                    "color: #60a5fa; font-weight: bold",
                    "color: #94a3b8",
                    "color: #4ade80; font-weight: bold"
                );
            });

            // ✅ SHOW EMAILS LANG SA CONSOLE
            console.log("=== TICKET EMAILS ===");
            myTickets.forEach(ticket => {
                console.log(
                    `%c${ticket.ticketNumber}%c → %c${ticket.email || "NO EMAIL"}`,
                    "color: #60a5fa; font-weight: bold",
                    "color: #94a3b8",
                    "color: #f472b6; font-weight: bold"
                );
            });

            setTickets(myTickets);
            organizeTickets(myTickets, search);

        } catch (error) {
            console.error("loadTickets error:", error);
            Swal.fire("Error", "Failed to load tickets", "error");
        }
    };

    const handleDrop = async (e, newStatus) => {
        e.preventDefault();
        if (!draggedTicket) return;
        if (draggedTicket.status === newStatus) { setDraggedTicket(null); return; }

        try {
            // ── 1. UPDATE TICKET STATUS ───────────────────────────────────────────
            const updatedTicket = { ...draggedTicket, status: newStatus };
            const res = await fetch(`${API_TICKETS}/${draggedTicket.ticketNumber}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(updatedTicket),
            });
            if (!res.ok) throw new Error();
            // ── 1.5 POST RESOLUTION ACTIVITY ─────────────────────────────────────
            // ── 1.5 POST RESOLUTION ACTIVITY ─────────────────────────────────────
            if (newStatus === "Resolved") {
                try {
                    const adminData = JSON.parse(localStorage.getItem("admin") || "{}");
                    const userData = JSON.parse(localStorage.getItem("user") || "{}");
                    const userId = adminData?.username || adminData?.email || userData?.username || userData?.email || "system";
                    const userName = adminData?.fullNames || adminData?.username || userData?.fullNames || userData?.username || "System";

                    const storageKey = `timer_pause_${draggedTicket.ticketNumber}`;
                    const pauseState = JSON.parse(localStorage.getItem(storageKey) || "{}");
                    const pausedAccum = pauseState.pausedAccum || 0;
                    const startedAt = draggedTicket.startedAt || draggedTicket.StartedAt;

                    const fmtDur = (s) => {
                        if (!s || s <= 0) return "00:00:00";
                        return `${String(Math.floor(s / 3600)).padStart(2, "0")}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
                    };

                    const timeSpentSecs = startedAt
                        ? Math.max(Math.floor((new Date() - new Date(startedAt)) / 1000) - pausedAccum, 0)
                        : null;

                    const deadline = draggedTicket.deadline || draggedTicket.Deadline;
                    const remainingSecs = deadline
                        ? Math.floor((new Date(deadline) - new Date()) / 1000)
                        : null;

                    // ✅ SAVE TO DB MUNA
                    await fetch(`${API_TICKETS}/${draggedTicket.ticketNumber}/timeTracking`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            timeSpent: timeSpentSecs ?? 0,
                            timeRemaining: remainingSecs !== null ? Math.max(remainingSecs, 0) : 0,
                        }),
                    }).catch(e => console.warn("timeTracking save failed:", e));

                    // ✅ THEN POST TO ACTIVITY FEED
                    const timeSpentLabel = timeSpentSecs !== null ? fmtDur(timeSpentSecs) : "Not tracked";
                    const remainingLabel = remainingSecs === null
                        ? "No deadline"
                        : remainingSecs <= 0
                            ? `⚠ Overdue by ${fmtDur(Math.abs(remainingSecs))}`
                            : fmtDur(remainingSecs);

                    await fetch(`${config.API_BASE_URL}/Activities`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            ticketNumber: draggedTicket.ticketNumber,
                            userId,
                            userName,
                            content: `✅ Ticket Resolved\n⏱ Time Spent: ${timeSpentLabel}\n⏳ Time Remaining at close: ${remainingLabel}`,
                        }),
                    });

                    console.log(`%c✅ Resolution logged%c | TimeSpent: ${timeSpentLabel} | Remaining: ${remainingLabel}`,
                        "color: #4ade80; font-weight: bold", "color: #94a3b8");
                    // ✅ CLEAR TIMER — stop the clock
                    localStorage.removeItem(`timer_pause_${draggedTicket.ticketNumber}`);

                } catch (e) {
                    console.warn("Resolution activity post failed:", e);
                }
            }
            // ── 2. FIRE-AND-FORGET EMAIL ──────────────────────────────────────────
            if (draggedTicket.email) {
                const statusMessages = {
                    "In Progress": { headline: "Your ticket is now being worked on!", detail: "A support agent has picked up your ticket and is actively working on it." },
                    "Resolved": { headline: "Your ticket has been resolved!", detail: "Our support team has resolved your issue. If you still have concerns, feel free to submit a new ticket." },
                    "Closed": { headline: "Your ticket has been closed.", detail: "This ticket has been closed. Thank you for reaching out! If you need further help, submit a new ticket anytime." },
                    "Pending": { headline: "Your ticket is pending review.", detail: "Your ticket is currently queued and waiting to be assigned to a support agent." },
                };

                const statusInfo = statusMessages[newStatus];

                if (statusInfo) {
                    const payload = {
                        toEmail: draggedTicket.email,
                        toName: draggedTicket.name || "User",
                        subject: `[Ticket Update] ${draggedTicket.ticketNumber} - Status changed to ${newStatus}`,
                        body:
                            `Hi ${draggedTicket.name || "User"},\n\n` +
                            `${statusInfo.headline}\n\n` +
                            `${statusInfo.detail}\n\n` +
                            `TICKET DETAILS\n` +
                            `Ticket Number  : ${draggedTicket.ticketNumber}\n` +
                            `Subject        : ${draggedTicket.subject || "N/A"}\n` +
                            `Category       : ${draggedTicket.category || "N/A"}\n` +
                            `Priority       : ${draggedTicket.priority || "N/A"}\n` +
                            `Previous Status: ${draggedTicket.status}\n` +
                            `New Status     : ${newStatus}\n` +
                            `Updated On     : ${new Date().toLocaleString()}\n\n` +
                            `You can log in to the User Portal to view your ticket and track further updates.\n\n` +
                            `Best regards,\n` +
                            `Orca Helpdesk Team\n\n` +
                            `DO NOT REPLY TO THIS EMAIL.\n` +
                            `This is an automated message. Replies to this email will not be monitored or received.\n` +
                            `If you have concerns, please log in to the User Portal instead.`,
                    };

                    console.log("📧 Sending email payload:", payload);

                    // fire and forget — no await
                    fetch(`${config.API_BASE_URL}/Email/send`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(payload),
                    })
                        .then(async r => {
                            const data = await r.json().catch(() => ({}));
                            if (r.ok) {
                                console.log(
                                    `%cEmail sent%c -> %c${draggedTicket.email}%c [${draggedTicket.status} -> ${newStatus}]`,
                                    "color: #4ade80; font-weight: bold",
                                    "color: #94a3b8",
                                    "color: #f472b6; font-weight: bold",
                                    "color: #94a3b8"
                                );
                            } else {
                                console.warn(`Email API returned ${r.status}:`, data);
                            }
                        })
                        .catch(err => console.warn(`Email failed for ${draggedTicket.ticketNumber}:`, err.message));
                }
            } else {
                console.warn(`No email found for ticket ${draggedTicket.ticketNumber} — skipped`);
            }

            // ── 3. SUCCESS TOAST ──────────────────────────────────────────────────
            Swal.fire({
                icon: "success",
                title: "Status Updated!",
                html: `Ticket moved to <strong>${newStatus}</strong>${draggedTicket.email
                    ? `<br><small style="color:#94a3b8">Notifying ${draggedTicket.email}...</small>`
                    : `<br><small style="color:#f87171">No email on file</small>`
                    }`,
                timer: 2000,
                showConfirmButton: false,
            });

            loadTickets();

        } catch {
            Swal.fire("Error", "Failed to update ticket status", "error");
        }

        setDraggedTicket(null);
    };

    const viewTicket = (ticket) => { setSelectedTicket(ticket); setShowModal(true); };
    const closeModal = () => { setShowModal(false); setSelectedTicket(null); };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString();
    };

    const getPriorityColor = (priority) => {
        const colors = {
            Low: "bg-green-500",
            Medium: "bg-yellow-500",
            High: "bg-orange-500",
            Critical: "bg-red-500",
        };
        return colors[priority] || "bg-slate-500";
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-slate-100 p-6">
            <div className="max-w-8xl mx-auto">
                {/* HEADER */}
                <div className="flex justify-between items-center mb-8">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => handlePageChange("dashboard")}
                            className="p-2.5 rounded-xl bg-slate-800/80 hover:bg-slate-700 border border-slate-700/50 transition-all"
                        >
                            <ArrowLeft size={20} />
                        </button>
                        <div>
                            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                                My Tasks
                            </h1>
                            <p className="text-slate-400 text-sm mt-1">
                                Tickets assigned to {currentUser?.fullNames || "you"}
                            </p>
                        </div>
                    </div>

                    {/* STATS */}
                    <div className="flex gap-3">
                        <div className="px-4 py-2 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
                            <div className="text-xs text-yellow-400">Pending</div>
                            <div className="text-xl font-bold text-yellow-400">{filteredTickets.pending.length}</div>
                        </div>
                        <div className="px-4 py-2 bg-blue-500/10 border border-blue-500/30 rounded-xl">
                            <div className="text-xs text-blue-400">In Progress</div>
                            <div className="text-xl font-bold text-blue-400">{filteredTickets.inProgress.length}</div>
                        </div>
                        <div className="px-4 py-2 bg-green-500/10 border border-green-500/30 rounded-xl">
                            <div className="text-xs text-green-400">Resolved</div>
                            <div className="text-xl font-bold text-green-400">{filteredTickets.resolved.length}</div>
                        </div>
                    </div>
                </div>

                {/* SEARCH */}
                <div className="mb-6 relative w-80">
                    <Search className="absolute left-4 top-3.5 text-slate-400" size={18} />
                    <input
                        className="w-full pl-12 pr-4 py-3 bg-slate-800/60 border border-slate-700/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                        placeholder="Search my tickets..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>

                {/* KANBAN BOARD */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 h-[90vh]">
                    <Column
                        title="Pending"
                        color="yellow"
                        tickets={filteredTickets.pending}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, "Pending")}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        onView={viewTicket}
                        getPriorityColor={getPriorityColor}
                        formatDate={formatDate}
                    />
                    <Column
                        title="In Progress"
                        color="blue"
                        tickets={filteredTickets.inProgress}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, "In Progress")}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        onView={viewTicket}
                        getPriorityColor={getPriorityColor}
                        formatDate={formatDate}
                    />
                    <Column
                        title="Resolved"
                        color="green"
                        tickets={filteredTickets.resolved}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, "Resolved")}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        onView={viewTicket}
                        getPriorityColor={getPriorityColor}
                        formatDate={formatDate}
                    />
                    <Column
                        title="Closed"
                        color="red"
                        tickets={filteredTickets.closed}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, "Closed")}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        onView={viewTicket}
                        getPriorityColor={getPriorityColor}
                        formatDate={formatDate}
                    />
                </div>

                {/* NO TICKETS MESSAGE */}
                {tickets.length === 0 && (
                    <div className="text-center py-20">
                        <FileText size={64} className="mx-auto mb-4 opacity-20" />
                        <p className="text-xl text-slate-400">No tickets assigned to you yet</p>
                    </div>
                )}
            </div>

            {/* VIEW MODAL */}
            {showModal && selectedTicket && (
                <TicketModal
                    ticket={selectedTicket}
                    onClose={closeModal}
                    formatDate={formatDate}
                    getPriorityColor={getPriorityColor}
                />
            )}
        </div>
    );
}

// ===== COLUMN COMPONENT =====
function Column({ title, color, tickets, onDragOver, onDrop, onDragStart, onDragEnd, onView, getPriorityColor, formatDate }) {
    const colorClasses = {
        yellow: {
            border: "border-yellow-500/30",
            bg: "bg-yellow-500/5",
            text: "text-yellow-400",
            badge: "bg-yellow-500/20 text-yellow-400"
        },
        blue: {
            border: "border-blue-500/30",
            bg: "bg-blue-500/5",
            text: "text-blue-400",
            badge: "bg-blue-500/20 text-blue-400"
        },
        green: {
            border: "border-green-500/30",
            bg: "bg-green-500/5",
            text: "text-green-400",
            badge: "bg-green-500/20 text-green-400"
        },
        red: {
            border: "border-red-500/30",
            bg: "bg-red-500/5",
            text: "text-red-400",
            badge: "bg-red-500/20 text-red-400"
        }
    };

    const colors = colorClasses[color];

    return (
        <div
            onDragOver={onDragOver}
            onDrop={onDrop}
            className={`flex-1 overflow-y-auto rounded-2xl border ${colors.border} ${colors.bg} p-4`}
        >
            <div className="shrink-0 flex items-center justify-between mb-4">
                <h3 className={`font-semibold text-lg ${colors.text}`}>{title}</h3>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${colors.badge}`}>
                    {tickets.length}
                </span>
            </div>

            <div className="flex-1 overflow-y-auto space-y-3 pr-1">
                {tickets.map((ticket) => (
                    <TicketCard
                        key={ticket.ticketNumber}
                        ticket={ticket}
                        onDragStart={onDragStart}
                        onDragEnd={onDragEnd}
                        onView={onView}
                        getPriorityColor={getPriorityColor}
                        formatDate={formatDate}
                    />
                ))}
                {tickets.length === 0 && (
                    <div className="text-center py-8 text-slate-500 text-sm">Drop tickets here</div>
                )}
            </div>
        </div>
    );
}

// ===== TICKET CARD COMPONENT =====
function TicketCard({ ticket, onDragStart, onDragEnd, onView, getPriorityColor, formatDate }) {
    const storageKey = `timer_pause_${ticket.ticketNumber}`;

    const getPauseState = () => {
        try { return JSON.parse(localStorage.getItem(storageKey)) || {}; } catch { return {}; }
    };

    const [now, setNow] = useState(new Date());
    const [isPaused, setIsPaused] = useState(() => getPauseState().isPaused || false);
    const [pausedAccum, setPausedAccum] = useState(() => getPauseState().pausedAccum || 0);
    const [pausedAt, setPausedAt] = useState(() => {
        const d = getPauseState().pausedAt;
        return d ? new Date(d) : null;
    });

    useEffect(() => {
        if (isPaused) return;
        const interval = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(interval);
    }, [isPaused]);

    const handlePauseResume = async (e) => {
        e.stopPropagation();

        if (isPaused) {
            const { value: resumeNote, isConfirmed } = await Swal.fire({
                title: "▶ Resume Timer?",
                html: `<div style="font-size:13px;color:#94a3b8;margin-bottom:8px">Add a note for resuming <strong style="color:#60a5fa">${ticket.ticketNumber}</strong></div>
                   <textarea id="swal-resume-note" class="swal2-textarea" placeholder="e.g. Back from break, continuing investigation..." style="font-size:13px;background:#1e293b;color:#f1f5f9;border:1px solid #334155;resize:none;height:80px;width:100%;box-sizing:border-box;padding:8px;border-radius:8px"></textarea>`,
                showCancelButton: true,
                confirmButtonText: "▶ Resume",
                cancelButtonText: "Cancel",
                confirmButtonColor: "#22c55e",
                cancelButtonColor: "#64748b",
                background: "#1e293b",
                color: "#f1f5f9",
                preConfirm: () => document.getElementById("swal-resume-note").value.trim() || "Resumed working on ticket.",
            });
            if (!isConfirmed) return;

            const addedPause = pausedAt ? Math.floor((new Date() - pausedAt) / 1000) : 0;
            const newAccum = pausedAccum + addedPause;
            const fmtDur = (s) => `${String(Math.floor(s / 3600)).padStart(2, "0")}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
            const pausedDur = fmtDur(Math.max(addedPause, 0));
            setPausedAccum(newAccum);
            setPausedAt(null);
            setIsPaused(false);
            setNow(new Date());
            localStorage.setItem(storageKey, JSON.stringify({ isPaused: false, pausedAccum: newAccum, pausedAt: null }));
            await postTimerActivity(`▶ Resumed | Paused for ${pausedDur}\n📝 ${resumeNote}`);

        } else {
            const { value: pauseNote, isConfirmed } = await Swal.fire({
                title: "⏸ Pause Timer?",
                html: `<div style="font-size:13px;color:#94a3b8;margin-bottom:8px">Why are you pausing <strong style="color:#60a5fa">${ticket.ticketNumber}</strong>?</div>
                   <textarea id="swal-pause-note" class="swal2-textarea" placeholder="e.g. Waiting for user response, on lunch break..." style="font-size:13px;background:#1e293b;color:#f1f5f9;border:1px solid #334155;resize:none;height:80px;width:100%;box-sizing:border-box;padding:8px;border-radius:8px"></textarea>`,
                showCancelButton: true,
                confirmButtonText: "⏸ Pause",
                cancelButtonText: "Cancel",
                confirmButtonColor: "#eab308",
                cancelButtonColor: "#64748b",
                background: "#1e293b",
                color: "#f1f5f9",
                preConfirm: () => document.getElementById("swal-pause-note").value.trim() || "Timer paused.",
            });
            if (!isConfirmed) return;

            const pAt = new Date();
            setPausedAt(pAt);
            setIsPaused(true);
            localStorage.setItem(storageKey, JSON.stringify({ isPaused: true, pausedAccum, pausedAt: pAt.toISOString() }));
            await postTimerActivity(`⏸ Paused at ${pAt.toLocaleTimeString()}\n📝 ${pauseNote}`);
        }
    };

    const postTimerActivity = async (message) => {
        try {
            const adminData = JSON.parse(localStorage.getItem("admin") || "{}");
            const userData = JSON.parse(localStorage.getItem("user") || "{}");
            const userId = adminData?.username || adminData?.email || userData?.username || userData?.email || "system";
            const userName = adminData?.fullNames || adminData?.username || userData?.fullNames || userData?.username || "System";

            await fetch(`${config.API_BASE_URL}/Activities`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    ticketNumber: ticket.ticketNumber,
                    userId,
                    userName,
                    content: message,
                }),
            });
        } catch (e) {
            console.warn("Timer activity post failed:", e);
        }
    };

    const getTimeSpent = () => {
        if (ticket.status === "Resolved" || ticket.status === "Closed") return null;
        if (!ticket.startedAt && !ticket.StartedAt) return null;
        const started = new Date(ticket.startedAt || ticket.StartedAt);
        const raw = Math.floor((now - started) / 1000) - pausedAccum;
        if (raw <= 0) return null;
        const h = String(Math.floor(raw / 3600)).padStart(2, "0");
        const m = String(Math.floor((raw % 3600) / 60)).padStart(2, "0");
        const s = String(raw % 60).padStart(2, "0");
        return `${h}:${m}:${s}`;
    };

    const timeSpent = getTimeSpent();

    return (
        <div
            draggable
            onDragStart={(e) => onDragStart(e, ticket)}
            onDragEnd={onDragEnd}
            className="bg-slate-800/80 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4 cursor-move hover:border-blue-500/50 transition-all group"
        >
            <div className={`h-1 w-full ${getPriorityColor(ticket.priority)} rounded-full mb-3`} />

            <div className="flex items-center justify-between mb-2">
                <span className="font-mono text-sm text-blue-400 font-medium">{ticket.ticketNumber}</span>
                <button
                    onClick={() => onView(ticket)}
                    className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-slate-700 transition-all"
                >
                    <Eye size={14} className="text-slate-400" />
                </button>
            </div>

            <h4 className="font-medium text-sm mb-2 line-clamp-2">{ticket.subject}</h4>

            {ticket.category && (
                <span className="inline-block px-2 py-1 bg-slate-700/50 rounded text-xs text-slate-300 mb-3">
                    {ticket.category}
                </span>
            )}

            {timeSpent && (() => {
                const deadline = ticket.deadline || ticket.Deadline;
                const remainingSecs = deadline
                    ? Math.floor((new Date(deadline) - now) / 1000) + (isPaused && pausedAt ? Math.floor((new Date() - pausedAt) / 1000) : 0)
                    : null;
                const fmtDur = (s) => {
                    if (s <= 0) return "00:00:00";
                    return `${String(Math.floor(s / 3600)).padStart(2, "0")}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
                };

                return (
                    <div className="flex gap-1.5 mb-2">
                        {/* LEFT — Elapsed */}
                        <div className={`flex-1 flex flex-col gap-0.5 px-2.5 py-2 border rounded-lg transition-all ${isPaused ? "bg-yellow-500/10 border-yellow-500/20" : "bg-blue-500/10 border-blue-500/20"
                            }`}>
                            <div className="flex items-center justify-between">
                                <span className="text-xs text-slate-500">elapsed</span>
                                <button
                                    onClick={handlePauseResume}
                                    title={isPaused ? "Resume" : "Pause"}
                                    className={`text-xs px-1 py-0.5 rounded border font-medium transition-all leading-none ${isPaused
                                        ? "bg-green-500/10 text-green-400 border-green-500/30 hover:bg-green-500/20"
                                        : "bg-yellow-500/10 text-yellow-400 border-yellow-500/30 hover:bg-yellow-500/20"
                                        }`}
                                >
                                    {isPaused ? "▶" : "⏸"}
                                </button>
                            </div>
                            <span className={`font-mono text-xs font-bold ${isPaused ? "text-yellow-400" : "text-blue-400"}`}>
                                {timeSpent}
                            </span>
                            {isPaused && <span className="text-yellow-600 text-xs italic animate-pulse leading-none">paused</span>}
                        </div>

                        {/* RIGHT — Remaining */}
                        <div className="flex-1 flex flex-col gap-0.5 px-2.5 py-2 bg-slate-700/30 border border-slate-700/40 rounded-lg">
                            <span className="text-xs text-slate-500">remaining</span>
                            {remainingSecs === null
                                ? <span className="font-mono text-xs font-bold text-slate-600">—</span>
                                : remainingSecs <= 0
                                    ? <span className="font-mono text-xs font-bold text-red-400 animate-pulse">⚠ Overdue</span>
                                    : <span className={`font-mono text-xs font-bold ${remainingSecs < 3600 ? "text-red-400" :
                                        remainingSecs < 86400 ? "text-yellow-400" : "text-green-400"
                                        }`}>{fmtDur(remainingSecs)}</span>
                            }
                        </div>
                    </div>
                );
            })()}

            <div className="flex items-center justify-between text-xs text-slate-400 mt-3 pt-3 border-t border-slate-700/50">
                <div className="flex items-center gap-1">
                    <Calendar size={12} />
                    <span>{formatDate(ticket.createdAt)}</span>
                </div>
                {ticket.priority && (
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPriorityColor(ticket.priority)} text-white`}>
                        {ticket.priority}
                    </span>
                )}
            </div>
        </div>
    );
}

// ===== TICKET MODAL COMPONENT =====